import { faker } from "@faker-js/faker";
import dayjs from "dayjs";
import { mockContact, type Contact } from "types/contact";
import type { LandlordFinancialsSummary } from "types/landlord-financials";
import type { PaginationMeta } from "types/pagination";
import { mockPropertyWithTenancies, type Property } from "types/property";
import type { RentScheduleEntryType } from "types/rent-schedule-entry";
import { type Tenancy } from "types/tenancy";
import { apiClient } from "utils/api-client/index";
import { createModel } from "utils/models";

export type OverviewData = {
  properties: Property<{ tenancies: Tenancy[] }>[];
  summary: LandlordFinancialsSummary;
  agency: Contact;
};

export type TenancyAgreementSummaryData = {
  total: number;
  occupied: number;
  vacant: number;
};

export type LandlordProperties = {
  id: string;
  display_name: string;
  status: {
    id: string;
    label: string;
  };
  is_outgoing: boolean;
  property_tenancy_id: string;
  property_tenancy_name: string;
  agreement_start_date: string | null;
  agreement_end_date: string | null;
  frequency_unit: { id: RentScheduleEntryType; label: string } | null;
  frequency_count: number | null;
  rent_amount: number | null;
  rent_arrears_amount: number;
  rent_arrears_days: number;
  paid_to_date: string | null;
  manage_by_agency_display_name: string | null;
  manage_by_agency_logo: string | null;
  __record_type: "property";
};

export type LandlordPropertiesData = {
  data: LandlordProperties[];
  pagination: PaginationMeta;
};

export const landlordPropertiesModel = createModel(
  "landlordPropertyOwnershipModel",
  {
    queries: {
      read: (searchParams: URLSearchParams, ownershipId: string) => ({
        queryKey: ["read", searchParams.toString(), ownershipId],
        queryFn: () => {
          const paramsString = new URLSearchParams(searchParams).toString();
          return apiClient.get<LandlordPropertiesData>(
            `/landlord-portal/${ownershipId}/tenancy-agreements?${paramsString}`,
          );
        },
        enabled: !!ownershipId,
      }),
    },
  },
);

export const landlordTenancyAgreementSummaryModel = createModel(
  "tenancyAgreementSummaryModel",
  {
    queries: {
      read: (ownershipId: string) => ({
        queryKey: ["tenancy-agreement-summary", ownershipId],
        queryFn: () => {
          return apiClient.get<TenancyAgreementSummaryData>(
            `/landlord-portal/${ownershipId}/tenancy-agreement-summary`,
          );
        },
        enabled: !!ownershipId,
      }),
    },
  },
);

export const landlordOverviewModel = createModel("landlordOverview", {
  queries: {
    read: () => ({
      queryKey: ["read"],
      queryFn: () => {
        return apiClient.get<OverviewData>(`/landlord/overview`);
      },
    }),
  },
});

export const mockLandlordOverviewResponse = () => {
  return {
    properties: Array.from({ length: 4 }).map(() =>
      mockPropertyWithTenancies(),
    ),
    agency: mockContact(),
    summary: {
      period_start_date: dayjs().startOf("month").format("YYYY-MM-DD"),
      period_end_date: dayjs().endOf("month").format("YYYY-MM-DD"),
      opening_balance: faker.number.int({ min: 2000, max: 10000 }),
      money_in: faker.number.int({ min: 100, max: 1000 }),
      money_out: faker.number.int({ min: 100, max: 1000 }),
      net_balance: faker.number.int({ min: 100, max: 1000 }),
      bills_outstanding: faker.number.int({ min: 100, max: 1000 }),
      reserved_funds: faker.number.int({ min: 100, max: 1000 }),
    },
  };
};
